import {
  Mail,
  Lock,
  User,
  Upload,
  Phone,
  Building2,
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useAuthStore from "../../store/authStore";
import { Button } from "@/components/ui/button";
import BasicAlert from "@/components/BasicAlert";

type RegistrationStep = 1 | 2 | 3;
type Plan = "basic" | "pro" | "enterprise";
type BusinessType = "restaurant" | "cafe" | "food-truck" | "bakery" | "other";

interface PlanDetails {
  name: string;
  price: string;
  features: string[];
}

const SignUpPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>(1);
  const [selectedPlan, setSelectedPlan] = useState<Plan>("basic");
  const { signup, loading, error } = useAuthStore();

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    restaurantName: "",
    address: "",
    phone: "",
    businessType: "" as BusinessType,
  });

  const plans: Record<Plan, PlanDetails> = {
    basic: {
      name: "Basic",
      price: "Free",
      features: ["Digital Menu", "QR Code Generation", "Basic Analytics"],
    },
    pro: {
      name: "Pro",
      price: "$29/month",
      features: [
        "All Basic Features",
        "Custom Branding",
        "Advanced Analytics",
        "Priority Support",
      ],
    },
    enterprise: {
      name: "Enterprise",
      price: "Custom",
      features: [
        "All Pro Features",
        "API Access",
        "Dedicated Support",
        "Custom Integration",
      ],
    },
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignupForm = async () => {
    try {
      await signup(formData);
    } catch (error) {
      console.error("Error creating user or restaurant:", error);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t("step")} {registrationStep} {t("of")} 3
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
        <div
          className="h-full bg-[#F39C12] rounded-full transition-all duration-300"
          style={{ width: `${(registrationStep / 3) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        setRegistrationStep(2);
      }}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("fullName")}
        </label>
        <div className="relative">
          <User
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("emailAddress")}
        </label>
        <div className="relative">
          <Mail
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            dir="ltr"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("password")}
        </label>
        <div className="relative">
          <Lock
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("confirmPassword")}
        </label>
        <div className="relative">
          <Lock
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-[#2C3E50] hover:bg-[#2C3E50]/90 text-white py-3 rounded-lg font-medium transition-colors"
      >
        {t("nextPlan")}
      </button>
    </form>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <button
        onClick={() => setRegistrationStep(1)}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#F39C12] transition-colors mb-4"
      >
        {isRTL ? (
          <ArrowRight className="h-4 w-4" />
        ) : (
          <ArrowLeft className="h-4 w-4" />
        )}
        {t("backToAccount")}
      </button>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(Object.keys(plans) as Plan[]).map((planKey) => {
          const plan = plans[planKey];
          return (
            <div
              key={planKey}
              className={`p-6 rounded-lg border-2 transition-all cursor-pointer ${
                selectedPlan === planKey
                  ? "border-[#F39C12] bg-[#F39C12]/5"
                  : "border-gray-200 dark:border-gray-700 hover:border-[#F39C12]/50"
              }`}
              onClick={() => setSelectedPlan(planKey)}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <p className="text-2xl font-bold mt-2">{plan.price}</p>
                </div>
                {selectedPlan === planKey && (
                  <Check className="text-[#F39C12] h-6 w-6" />
                )}
              </div>
              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li
                    key={index}
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    • {feature}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <button
        onClick={() => setRegistrationStep(3)}
        className="w-full bg-[#2C3E50] hover:bg-[#2C3E50]/90 text-white py-3 rounded-lg font-medium transition-colors"
      >
        {t("nextDetails")}
      </button>
    </div>
  );

  const renderStep3 = () => (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <button
        onClick={() => setRegistrationStep(2)}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#F39C12] transition-colors mb-4"
      >
        {isRTL ? (
          <ArrowRight className="h-4 w-4" />
        ) : (
          <ArrowLeft className="h-4 w-4" />
        )}
        {t("backToPlans")}
      </button>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("restaurantName")}
        </label>
        <div className="relative">
          <Building2
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="text"
            name="restaurantName"
            value={formData.restaurantName}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("address")}
        </label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("phone")}
        </label>
        <div className="relative">
          <Phone
            className={`absolute ${
              isRTL ? "right-3" : "left-3"
            } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
          />
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`${
              isRTL ? "pr-10" : "pl-10"
            } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("businessType")}
        </label>
        <select
          name="businessType"
          value={formData.businessType}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">{t("selectType")}</option>
          <option value="restaurant">{t("restaurant")}</option>
          <option value="cafe">{t("cafe")}</option>
          <option value="food-truck">{t("foodTruck")}</option>
          <option value="bakery">{t("bakery")}</option>
          <option value="other">{t("other")}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("uploadLogo")}
        </label>
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-[#F39C12] transition-colors">
          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("Click or drag file to upload")}
          </p>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full h-12 bg-[#2C3E50] hover:bg-[#2C3E50]/90 text-white py-5 rounded-lg font-medium transition-colors"
        onClick={handleSignupForm}
        disabled={loading}
      >
        {loading ? <Loader2 className="animate-spin" /> : t("completeRegistration")}
      </Button>
      {error ? <BasicAlert type="error" description={error} /> : null}
    </form>
  );

  return (
    <>
      {renderProgressBar()}
      {registrationStep === 1 && renderStep1()}
      {registrationStep === 2 && renderStep2()}
      {registrationStep === 3 && renderStep3()}

      <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
        {t("alreadyHaveAccount")}
        <a
          href="/auth"
          className="mx-2 text-[#F39C12] hover:underline font-medium"
        >
          {t("logIn")}
        </a>
      </p>
    </>
  );
};

export default SignUpPage;
